import React, { useState } from "react";
import { motion } from "framer-motion";
import { UtensilsCrossed, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO =
  "https://images.unsplash.com/photo-1555244162-803834f70033?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxjYXRlcmluZyUyMGZvb2QlMjBwcmVwYXJhdGlvbnxlbnwwfHx8fDE3OTA2NjI0NjB8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Hoş geldiniz");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-3 mb-10">
            <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/30">
              <UtensilsCrossed className="h-6 w-6" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-xl tracking-tight leading-none">ÇANKAYA CATERİNG</div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mt-1">
                Catering ERP v2.4
              </div>
            </div>
          </div>

          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Tekrar hoş geldiniz</h1>
          <p className="text-sm text-muted-foreground mb-8">
            Cari hesap, günlük ciro ve maliyet takibinize devam edin.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                E-posta
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-username-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@sirket.com"
                className="h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Şifre
              </Label>
              <Input
                id="pw"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 rounded-full font-semibold text-base transition-transform active:scale-[0.98]"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Giriş Yap"}
            </Button>
          </form>

          <div className="mt-6 text-xs text-muted-foreground border border-border rounded-lg p-3 bg-muted/40">
            Hesabınız yoksa yöneticinizden kullanıcı oluşturmasını isteyin.
          </div>
        </motion.div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="catering" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/55 to-black/30" />
        <div className="absolute bottom-0 left-0 p-12 text-white max-w-lg">
          <div className="text-primary-foreground/90 text-xs font-semibold uppercase tracking-widest mb-3">
            Yemek İşletmeleri İçin
          </div>
          <h2 className="font-heading text-4xl font-bold leading-tight mb-4">
            Her porsiyonu ciroya, her ciroyu kâra çevirin.
          </h2>
          <p className="text-white/70 text-sm leading-relaxed">
            Günlük kişi sayısından cari hesap takibine, maliyet yönetiminden kâr-zarar raporlarına kadar
            tüm operasyonunuz tek ekranda.
          </p>
        </div>
      </div>
    </div>
  );
}
